import React, { useState, useEffect, useRef, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useAuthModal } from "../../App";
import { useGameSession, useMidnightCountdown, MAX_ATTEMPTS } from "../../hooks/useGameSession";
import { useNextChallenge } from "../../hooks/useNextChallenge";
import ResultModal from "../../components/ResultModal";
import AchievementPopup from "../../components/AchievementPopup";
import ConfirmDialog from "../../components/ConfirmDialog";
import Tooltip from "../../components/Tooltip";
import HintTile from "../../components/HintTile";
import "../../styles/GuessNpcPage.css";

const HINT_LETTER_AT = 2;
const HINT_LENGTH_AT = 3;

/* ── Attribute display config ─────────────────────── */
const ATTR_META = [
  { key: "race",      label: "Race",      tooltip: "The race of the NPC" },
  { key: "gender",    label: "Gender",    tooltip: "The gender of the NPC" },
  { key: "faction",   label: "Faction",   tooltip: "Alliance, Horde, or Neutral" },
  { key: "npc_class", label: "Class",     tooltip: "The closest matching class" },
  { key: "npc_type",  label: "Affiliation",      tooltip: "Relationship with the player across the story" },
  { key: "zone",      label: "Continent", tooltip: "The continent most associated with this NPC" },
];

/* ── Tooltip lines for a result cell ─────────────── */
function cellTooltipLines(result, label, value) {
  if (result === "correct") return [
    { icon: "✔", text: label + ": " + value, type: "highlight" },
    { text: "Exact match", type: "muted" },
  ];
  if (result === "partial") return [
    { icon: "◑", text: label + ": " + value, type: "highlight" },
    { text: "Partially correct", type: "muted" },
  ];
  return [
    { icon: "✕", text: label + ": " + value, type: "highlight" },
    { text: "Not a match", type: "muted" },
  ];
}

/* ── Tooltip lines for a column header ───────────── */
function headerTooltipLines(label, description) {
  return [
    { icon: "◈", text: label, type: "title" },
    { text: description, type: "muted" },
  ];
}

function resultClass(result) {
  if (result === "correct") return "gn-cell--correct";
  if (result === "partial") return "gn-cell--partial";
  return "gn-cell--wrong";
}

/* ── Single guess row (comparison table) ──────────── */
function NpcGuessRow({ guess, animate }) {
  const comparison = guess.comparison || {};
  return (
    <div className={`gn-guess-row ${animate ? "gn-guess-row--animate" : ""}`}>
      <div className="gn-guess-name" title={guess.npc?.name}>{guess.npc?.name || "???"}</div>
      <div className="gn-guess-cells">
        {ATTR_META.map(({ key, label }, i) => {
          const cell = comparison[key];
          if (!cell) return null;
          const value = cell.value || "—";
          const result = cell.result || "wrong";
          return (
            <Tooltip key={key} lines={cellTooltipLines(result, label, value)}>
              <div
                className={`gn-cell ${resultClass(result)}`}
                style={animate ? { animationDelay: `${i * 0.08}s` } : undefined}
              >
                <span className="gn-cell-label">{label}</span>
                <span className="gn-cell-value">{value}</span>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

/* ── Empty placeholder row ─────────────────────────── */
function NpcEmptyRow({ index }) {
  return (
    <div className="gn-guess-row gn-guess-row--empty">
      <div className="gn-guess-name gn-guess-name--empty">#{index + 1}</div>
      <div className="gn-guess-cells">
        {ATTR_META.map(({ key, label }) => (
          <div key={key} className="gn-cell gn-cell--empty">
            <span className="gn-cell-label">{label}</span>
            <span className="gn-cell-value">—</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────── */
export default function GuessNpcPage() {
  const navigate  = useNavigate();
  const { open: openAuth } = useAuthModal();
  const countdown = useMidnightCountdown();
  const inputRef  = useRef(null);

  const s = useGameSession({
    gameName:       "npcs",
    itemIdKey:      "id",
    correctNameKey: "correct_npc_name",
    dailyKey:       "daily_npc",
  });

  // ── Next Challenge tracking ──
  const { markCompleted, nextChallenge, allCompleted } = useNextChallenge("npcs", s.gameMode);

  useEffect(() => {
    if (s.game.finished) markCompleted();
  }, [s.game.finished, markCompleted]);

  const hints = s.game.hints || {};

  const letterTooltip = useMemo(() => {
    if (hints.first_letter) return [
      { icon: "✦", text: "First letter: " + hints.first_letter, type: "highlight" },
    ];
    if (s.attemptsUsed >= HINT_LETTER_AT) return [
      { icon: "⏳", text: "Revealed — make a guess to refresh", type: "muted" },
    ];
    const rem = Math.max(0, HINT_LETTER_AT - s.attemptsUsed);
    return [
      { icon: "🔒", text: "First Letter hint", type: "title" },
      { text: "Available after " + HINT_LETTER_AT + " attempts", type: "muted" },
      { text: rem + " attempt" + (rem === 1 ? "" : "s") + " remaining", type: "countdown" },
    ];
  }, [hints.first_letter, s.attemptsUsed]);

  const lengthTooltip = useMemo(() => {
    if (hints.name_length) return [
      { icon: "✦", text: "Name length: " + hints.name_length + " characters", type: "highlight" },
    ];
    if (s.attemptsUsed >= HINT_LENGTH_AT) return [
      { icon: "⏳", text: "Revealed — make a guess to refresh", type: "muted" },
    ];
    const rem = Math.max(0, HINT_LENGTH_AT - s.attemptsUsed);
    return [
      { icon: "🔒", text: "Name Length hint", type: "title" },
      { text: "Available after " + HINT_LENGTH_AT + " attempts", type: "muted" },
      { text: rem + " attempt" + (rem === 1 ? "" : "s") + " remaining", type: "countdown" },
    ];
  }, [hints.name_length, s.attemptsUsed]);

  /* Answers are objects with { npc, comparison, correct, attempt_number } */
  const guessRows = s.game.answers || [];

  /* Random guess — pick a random NPC via backend */
  const pendingRandomGuess = useRef(false);

  const doRandomGuess = async () => {
    if (s.submitting) return;
    try {
      const guessedIds = guessRows.map(g => g.npc?.id).filter(Boolean);
      const exclude = guessedIds.length > 0 ? `?exclude=${guessedIds.join(",")}` : "";
      const res = await fetch(`/random_npc${exclude}`);
      const data = await res.json();
      if (!res.ok || !data.id) return;

      pendingRandomGuess.current = true;
      s.handleSelect({ id: data.id, name: data.name });
    } catch (e) {
      console.error("Random guess failed:", e);
    }
  };

  /* Auto-submit once selectedItem updates from random pick */
  useEffect(() => {
    if (pendingRandomGuess.current && s.selectedItem && !s.submitting) {
      pendingRandomGuess.current = false;
      s.makeGuess();
    }
  }, [s.selectedItem]); // eslint-disable-line
  useEffect(() => {
    if (!s.submitting && !s.game.finished && inputRef.current) {
      inputRef.current.focus();
    }
  }, [s.submitting, s.game.finished]);

  return (
    <div className="gs-root">
      <Helmet>
        <title>Azerodle | Guess the NPC</title>
        <meta name="description" content="Guess a character or NPC from World of Warcraft. Test your knowledge of Azeroth." />
      </Helmet>
      <div className="gs-wrapper">

        {s.isGuest && (
          <div className="gs-guest-banner">
            <span>Playing as <strong>guest</strong> — rewards and statistics unavailable</span>
            <button className="gs-ghost-btn" onClick={openAuth}>Sign up</button>
          </div>
        )}

        <div className="gs-page-title">
          <h1 className="gs-title">Guess the Character </h1>
        </div>

        {s.initialLoading ? (
          <div className="gs-panel gs-panel--ready">
            <div className="gs-panel-spinner"><div className="gs-spinner" /></div>
          </div>
        ) : (
          <div className="gs-panel gs-panel--ready">
            <div className="gs-panel-content">

              {/* Mode switcher */}
              <div className="gs-mode-row">
                {["classic", "retail"].map(mode => (
                  <button
                    key={mode}
                    className={"gs-mode-btn " + (s.gameMode === mode ? "active" : "")}
                    onClick={() => s.switchMode(mode)}
                  >
                    {mode === "retail" ? "Retail" : "Classic"}
                  </button>
                ))}
              </div>

              <div className="gs-divider" />

              <div className={[
                "gs-panel-body",
                s.showBody ? "gs-panel-body--ready" : "",
                s.fading   ? "gs-panel-body--fading" : "",
              ].join(" ")}>
              {s.contentReady && (<>

                {/* ── Comparison board — visible only while playing ── */}
                {!s.game.finished && (<>

                {/* ── Hints — HintTile above board ── */}
                <div className="gn-hints-row">
                  <HintTile
                    label="First letter:"
                    value={hints.first_letter}
                    revealed={!!hints.first_letter}
                    tooltipLines={letterTooltip}
                    animateHints={s.game.shouldAnimateHints}
                  />
                  <HintTile
                    label="Name Length:"
                    value={hints.name_length}
                    revealed={!!hints.name_length}
                    tooltipLines={lengthTooltip}
                    animateHints={s.game.shouldAnimateHints}
                  />
                </div>

                {/* Attribute column header */}
                <div className="gn-board-header">
                  {ATTR_META.map(({ key, label, tooltip }) => (
                    <Tooltip key={key} lines={headerTooltipLines(label, tooltip)}>
                      <div className="gn-board-header-col">
                        <span className="gn-board-header-label">{label}</span>
                        <span className="gn-board-header-tooltip-icon">?</span>
                      </div>
                    </Tooltip>
                  ))}
                </div>

                {/* 5 attempt rows — filled or empty */}
                <div className="gn-board">
                  {Array.from({ length: MAX_ATTEMPTS }, (_, i) => {
                    const guess = guessRows[i];
                    const isLast = guess && s.game.animateAttempts && i === guessRows.length - 1;
                    return guess
                      ? <NpcGuessRow key={s.gameMode + "-" + guess.attempt_number} guess={guess} animate={isLast} />
                      : <NpcEmptyRow key={s.gameMode + "-empty-" + i} index={i} />;
                  })}
                </div>

                <div className="gs-divider" />

                </>)}

                {/* ── Finished state ── */}
                {s.game.finished ? (
                  <div className={"gs-finished " + (s.didWin ? "win" : "lose")}>
                    <div className="gs-finished-icon">{s.didWin ? "🏆" : "💀"}</div>
                    <p className="gs-finished-title">{s.didWin ? "Correct!" : "Game Over"}</p>
                    <p className="gs-finished-sub">
                      {s.didWin
                        ? <>You guessed <strong>{s.correctAnswer?.npc?.name || s.correctAnswer?.user_answer}</strong> in{" "}
                            <strong>{s.correctAnswer?.attempt_number}</strong>{" "}
                            {s.correctAnswer?.attempt_number === 1 ? "attempt" : "attempts"}</>
                        : <>The correct character was <strong>{s.game.correctAnswerName || "???"}</strong></>
                      }
                    </p>
                    <div className="gs-finished-countdown">
                      <span className="gs-finished-countdown-label">Next Character in</span>
                      <span className="gs-finished-countdown-time">{countdown}</span>
                    </div>

                    {/* Next Challenge button */}
                    {nextChallenge ? (
                      <button
                        className="gs-btn-next-challenge"
                        onClick={() => {
                          if (nextChallenge.game === "npcs") {
                            s.switchMode(nextChallenge.mode);
                          } else {
                            navigate(nextChallenge.path + "?mode=" + nextChallenge.mode);
                          }
                        }}
                      >
                        <span className="gs-btn-next-challenge-text">Next Challenge</span>
                      </button>
                    ) : allCompleted ? (
                      <div className="gs-all-done">
                        <span className="gs-all-done-text">All daily challenges completed!</span>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  /* ── Input section ── */
                  <div className="gs-input-section">
                    <div className="gs-progress-wrap">
                      <div className="gs-progress-meta">
                        <span className="gs-progress-label">Attempts</span>
                        <span className={"gs-progress-count " + (s.attemptsUsed >= MAX_ATTEMPTS ? "exhausted" : "")}>
                          {s.attemptsUsed} / {MAX_ATTEMPTS}
                        </span>
                      </div>
                      <div className="gs-progress-track">
                        <div
                          className={"gs-progress-fill " + (s.game.shouldAnimateProgress ? "animate" : "")}
                          style={{ width: s.game.progressWidth + "%" }}
                        />
                      </div>
                    </div>

                    <div className="gn-search-wrap">
                      <input
                        ref={inputRef}
                        className="gs-input gn-search-input"
                        type="text"
                        placeholder="Type a character name…"
                        value={s.userAnswer}
                        onChange={s.handleChange}
                        onKeyDown={s.handleKeyDown}
                        disabled={s.submitting}
                        autoComplete="off"
                      />
                      {s.suggestions.length > 0 && (
                        <ul className="gn-suggestions">
                          {s.suggestions.map(npc => (
                            <li
                              key={npc.id}
                              className={`gn-suggestion-item ${s.selectedItem?.id === npc.id ? "gn-suggestion-item--active" : ""}`}
                              onMouseDown={() => s.handleSelect(npc)}
                            >
                              {npc.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <button
                      className={"gs-btn-gold " + (s.submitting ? "submitting" : "")}
                      onClick={s.makeGuess}
                      disabled={!s.selectedItem || s.submitting}
                    >
                      {s.submitting ? "Checking…" : "Submit Answear"}
                    </button>

                    <div className="gs-secondary-actions">
                      <button
                        className="gs-btn-secondary gs-btn-skip"
                        onClick={doRandomGuess}
                        disabled={s.submitting}
                      >
                        Random Guess
                      </button>
                      <button
                        className="gs-btn-secondary gs-btn-surrender"
                        onClick={() => s.setConfirmAction("surrender")}
                        disabled={s.submitting}
                      >
                        Surrender
                      </button>
                    </div>

                    {s.error && <p className="gs-error">⚠ {s.error}</p>}
                  </div>
                )}

              </>)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overlays */}
      {s.confirmAction && (
        <ConfirmDialog
          type={s.confirmAction}
          onConfirm={s.handleConfirm}
          onCancel={s.handleCancelConfirm}
        />
      )}

      {s.showModal && (
        <ResultModal
          result={s.lastResult}
          onClose={s.handleResultClose}
          isGuest={s.isGuest}
          onRegister={openAuth}
          itemName={s.correctAnswer?.npc?.name || s.game.correctAnswerName}
          itemLabel="NPC"
          gameName="npcs"
          gameMode={s.gameMode}
          answers={s.game.answers}
          stats={s.stats}
          xpResult={s.isGuest ? null : s.xpResult}
        />
      )}

      {s.showAchievement && s.achievementQueue.length > 0 && (
        <AchievementPopup
          achievement={s.achievementQueue[0]}
          onClose={s.handleAchievementClose}
        />
      )}
    </div>
  );
}